import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { readImportRows } from './productImportReport';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export function validateRows(result) {
  const { headers, rows } = result;
  if (!headers.length || !rows.length) throw new Error('This sheet has no data rows. Choose another sheet or file.');
  if (rows.length > 5000 || headers.length > 200) throw new Error('Use up to 5,000 rows and 200 columns per import.');
  if (new Set(headers).size !== headers.length || headers.some(h => !h.trim() || h.length > 300)) throw new Error('Column headers must be unique, non-blank and no longer than 300 characters.');
  return result;
}

export function parseCsv(file) {
  return new Promise((resolve, reject) => Papa.parse(file, {
    header: true, skipEmptyLines: true,
    complete: parsed => {
      try {
        if (parsed.errors.length || Object.keys(parsed.meta.renamedHeaders || {}).length) throw new Error('The CSV has duplicate headers or uneven columns. Correct it before importing.');
        resolve(validateRows(readImportRows(parsed)));
      } catch (error) { reject(error); }
    }, error: () => reject(new Error('Could not read this CSV file.')),
  }));
}

export async function readWorkbook(file) {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const zip = bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
    const ole = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1].every((value, i) => bytes[i] === value);
    const biff = bytes[0] === 0x09 && [0x00, 0x02, 0x04, 0x08].includes(bytes[1]);
    // SheetJS intentionally auto-detects CSV/HTML. Successful parsing alone
    // does not prove an uploaded .xls/.xlsx is an Excel workbook.
    if (!zip && !ole && !biff) throw new Error();
    const workbook = XLSX.read(bytes, { type: 'array', cellNF: true, cellDates: false, sheetRows: 5002, bookVBA: false, bookFiles: true });
    if (zip && !workbook.keys?.includes('xl/workbook.xml')) throw new Error();
    if (!workbook.SheetNames.length) throw new Error();
    delete workbook.files;
    delete workbook.keys;
    return workbook;
  } catch { throw new Error('Could not read this Excel workbook. Check that it is a valid, unencrypted .xlsx or .xls file.'); }
}

function cellText(cell, date1904) {
  if (!cell) return '';
  if (cell.f) throw new Error('Replace Excel formulas with values before importing this sheet.');
  if (cell.t === 'e') throw new Error('This sheet contains an Excel error cell. Correct it before importing.');
  if (cell.t === 'n' && XLSX.SSF.is_date(cell.z || '')) {
    const date = XLSX.SSF.parse_date_code(cell.v, { date1904 });
    if (!date) throw new Error('This sheet contains an invalid date cell.');
    return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
  }
  if (cell.t === 'n' && Math.abs(cell.v) > Number.MAX_SAFE_INTEGER) throw new Error('Long numeric identifiers must be stored as text in Excel to preserve every digit.');
  if (cell.t === 'n' && /^0+$/.test(cell.z || '')) return XLSX.utils.format_cell(cell);
  return cell.v == null ? '' : String(cell.v);
}

export function rowsFromSheet(workbook, sheetName) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet?.['!ref']) throw new Error('This sheet is empty. Choose another sheet.');
  const range = XLSX.utils.decode_range(sheet['!fullref'] || sheet['!ref']);
  if (range.e.r - range.s.r > 5000 || range.e.c - range.s.c >= 200) throw new Error('Use up to 5,000 rows and 200 columns per sheet. No rows have been imported.');
  const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);
  const values = [];
  for (let r = range.s.r; r <= range.e.r; r++) {
    const cells = [];
    for (let c = range.s.c; c <= range.e.c; c++) cells.push(cellText(sheet[XLSX.utils.encode_cell({ r, c })], date1904));
    values.push(cells);
  }
  const headers = values.shift();
  const rows = values.filter(cells => cells.some(c => c.trim())).map(cells => Object.fromEntries(headers.map((header, i) => [header, cells[i]])));
  return validateRows({ headers, rows });
}